import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES, isLocale, setLocale, type Locale } from '@/lib/i18n'

import { Container } from './primitives'

/**
 * Le sélecteur de langue — UN composant pour toutes les pages.
 *
 * Il en existait deux (les pastilles du site public, la liste déroulante du
 * back-office) et aucun ailleurs : un invité étranger qui scannait le QR de
 * sa table, un juré, un barman remplaçant restaient dans la langue devinée
 * par le navigateur. Le comportement est celui de `setLocale` : langue
 * enregistrée sur l'appareil, `<html lang>` mis à jour, i18next rebasculé —
 * et, comme les clés de requête portent la langue, les données traduites par
 * l'API se relisent d'elles-mêmes.
 *
 * Deux variantes :
 * - `pills` : DE | FR | EN dans un cadre, pour les en-têtes ;
 * - `minimal` : DE · FR · EN en texte seul, pour les écrans contraints
 *   (la télé de la salle, une page d'erreur).
 *
 * Les cibles font 40 px au doigt ; elles ne rétrécissent qu'avec une souris.
 */

/**
 * Le nom de chaque langue dans SA langue : on reconnaît « Deutsch » même
 * quand l'interface est en français. Ce n'est donc pas un texte à traduire.
 */
const NATIVE_NAME: Record<Locale, string> = {
  de: 'Deutsch',
  fr: 'Français',
  en: 'English',
}

// Souris ou pavé : précis, on peut resserrer. Au doigt, 40 px minimum.
// Classes écrites en entier : Tailwind ne voit pas une classe composée.
const FINE_TARGET =
  '[@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8'
const FINE_PADDING = '[@media(hover:hover)_and_(pointer:fine)]:px-3'

interface LanguageSwitcherProps {
  variant?: 'pills' | 'minimal'
  /** Les langues proposées — par défaut toutes celles de l'interface. */
  locales?: readonly string[]
  /** Affiche « Langue » devant le sélecteur (pied de page, menu). */
  withLabel?: boolean
  /** Après le changement : le back-office l'enregistre aussi sur le compte. */
  onChange?: (locale: Locale) => void
  className?: string
}

export function LanguageSwitcher({
  variant = 'pills',
  locales,
  withLabel = false,
  onChange,
  className,
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation()
  const available = (locales ?? SUPPORTED_LOCALES).filter(isLocale)
  if (available.length < 2) return null

  // `resolvedLanguage` vaut « de » même si le navigateur a annoncé « de-AT ».
  const current = i18n.resolvedLanguage ?? i18n.language
  const choose = (locale: Locale) => {
    if (locale === current) return
    void setLocale(locale)
    onChange?.(locale)
  }

  const minimal = variant === 'minimal'

  return (
    <div className={cn('flex shrink-0 items-center gap-2', className)}>
      {withLabel ? (
        <span className="overline text-content-muted">{t('footer.language')}</span>
      ) : null}
      <div
        role="group"
        aria-label={t('footer.language')}
        className={cn(
          'flex items-center',
          minimal ? 'gap-0.5' : 'overflow-hidden rounded-full border border-line',
        )}
      >
        {available.map((locale, index) => {
          const active = locale === current
          return (
            <span key={locale} className="flex items-center">
              {minimal && index > 0 ? (
                <span aria-hidden className="text-xs text-content-muted/60">
                  ·
                </span>
              ) : null}
              <button
                type="button"
                lang={locale}
                aria-pressed={active}
                title={NATIVE_NAME[locale]}
                onClick={() => choose(locale)}
                className={cn(
                  'inline-flex min-h-10 min-w-10 items-center justify-center text-xs transition',
                  FINE_TARGET,
                  minimal
                    ? cn(
                        'rounded-md px-1.5 tracking-wide',
                        active
                          ? 'text-content-primary underline decoration-champagne-400 underline-offset-4'
                          : 'text-content-muted hover:text-content-secondary',
                      )
                    : cn(
                        'px-2.5',
                        FINE_PADDING,
                        active
                          ? 'bg-bg-overlay text-content-primary'
                          : 'text-content-muted hover:text-content-secondary',
                      ),
                )}
              >
                {locale.toUpperCase()}
                {/* Le nom complet pour un lecteur d'écran — « DE » seul ne
                    dit rien à l'oreille. Le code reste en tête du nom
                    accessible : la commande vocale « DE » doit le trouver. */}
                <span className="sr-only"> — {NATIVE_NAME[locale]}</span>
              </button>
            </span>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Une bande en haut des pages qui n'ont pas d'en-tête : lien de vote, page
 * du juré, billet, commande à table, pages d'erreur.
 *
 * Dans le flux de la page, pas flottante : elle ne peut rien recouvrir,
 * quelle que soit la longueur d'un titre en allemand.
 */
export function LanguageBar({ className }: { className?: string }) {
  return (
    <Container className={cn('flex justify-end pt-3', className)}>
      <LanguageSwitcher />
    </Container>
  )
}

/**
 * Le coin de la télé de salle (`/ecran`).
 *
 * L'écran est fait pour être lu à quatre mètres, pas pour être manipulé :
 * le sélecteur est discret, et s'efface tout à fait après quelques secondes
 * sans souris ni toucher. Il revient au moindre geste ou à la tabulation.
 * La télé se règle plutôt une fois pour toutes avec `?lang=xx` dans son lien.
 */
export function IdleLanguageCorner({ idleMs = 4000 }: { idleMs?: number }) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    let timer = window.setTimeout(() => setVisible(false), idleMs)
    const wake = () => {
      setVisible(true)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setVisible(false), idleMs)
    }
    const events = ['pointermove', 'pointerdown', 'keydown', 'touchstart'] as const
    for (const name of events) window.addEventListener(name, wake, { passive: true })
    return () => {
      window.clearTimeout(timer)
      for (const name of events) window.removeEventListener(name, wake)
    }
  }, [idleMs])

  return (
    <div
      className={cn(
        'fixed bottom-2 right-2 z-50 rounded-full bg-bg-base/70 px-1 backdrop-blur transition-opacity duration-500',
        // Effacé mais atteignable : le focus clavier le fait réapparaître.
        visible ? 'opacity-100' : 'pointer-events-none opacity-0 focus-within:pointer-events-auto focus-within:opacity-100',
      )}
    >
      <LanguageSwitcher variant="minimal" />
    </div>
  )
}
